import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';

import { DEFAULT_ENTRY_LIMITS } from '../../../../constants/form.constants';
import { ITableConfig, ITableRow, SortEvent } from '../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { $digitalProperty, $digitalPropertyType } from '../../../../../../shared/constants/digital-properties';
import { TranslateService } from '../../../../translate/translate.service';
import { IDigitalProperty } from '../../../../../../shared/interfaces/digital-property.interface';
import { NgbTableUtilities } from '../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { SharedCommonUtility } from '../../../../../../shared/utils/common.utility';
import { $sortingOrder } from '../../../../../../shared/constants/sort';
import { Api } from '../../../../../../shared/constants/api';
import { ErrorHandlerService } from '../../../../services/error-handler.service';
import { UserAclService } from '../../../../services/user-acl.service';
import { AclSecurityAdapter } from '../../../../services/acl.service';
import { RequiredSecurities } from '../../../../../../shared/constants/required-securities';
import { IDropdownItem } from '../../../../components/table/ngb-table/cells/dropdown-cell/dropdown-cell.component';

@Component({
  selector: 'app-tenant-workspace-digital-properties-list',
  templateUrl: './digital-properties-list.component.html',
  standalone: false,
})
export class TenantWorkspaceDigitalPropertiesListComponent implements OnInit {
  public pageSizeLimits: readonly number[] = DEFAULT_ENTRY_LIMITS;
  public sort$: BehaviorSubject<SortEvent> = new BehaviorSubject<SortEvent>(null);
  public dpCount$: Observable<number>;
  public tableConfig: ITableConfig;
  public tableData$: Observable<ITableRow[]>;

  public pageSize$: BehaviorSubject<number>;
  public get pageSize(): number {
    return this.pageSize$.value;
  }

  public set pageSize(value: number) {
    this.pageSize$.next(value);
  }

  public page$: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  public get page(): number {
    return this.page$.value;
  }

  public set page(value: number) {
    this.page$.next(value);
  }

  public searchTerm$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public get searchTerm(): string {
    return this.searchTerm$.value;
  }

  public set searchTerm(value: string) {
    this.searchTerm$.next(value);
  }

  @Input() public digitalProperties$: Observable<IDigitalProperty[]>;

  constructor(
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private errorHandlerService: ErrorHandlerService,
    private userAclService: UserAclService,
  ) {
    this.pageSize$ = new BehaviorSubject<number>(this.pageSizeLimits[this.pageSizeLimits.length - 1]);
    this.tableConfig = this.getTableConfig();
  }

  private getTableConfig(): ITableConfig {
    return {
      columns: {
        [$digitalProperty.name]: {
          translationKey: 'digital_name_label',
          sortingEnabled: true,
          styles: {
            width: '20%',
          },
        },
        [$digitalProperty.type]: {
          translationKey: 'digital_type_label',
          sortingEnabled: true,
          styles: {
            width: '20%',
          },
        },
        [$digitalProperty.url]: {
          translationKey: 'table_column_digital_property_url',
          sortingEnabled: true,
        },
        actions: {
          translationKey: 'page_actions',
          styles: {
            width: '10%',
            maxWidth: '10%',
            minWidth: '5rem',
          },
        },
      },
      caption: this.translateService.instant('audit_digital_properties_label'),
      emptyState: {
        title: this.translateService.instant('no_digital_properties_in_this_workspace'),
        subtitle: this.translateService.instant(''),
      },
    };
  }

  private getActionsDropdownItems(entry: IDigitalProperty, canUpdateDigitalProperties: boolean): IDropdownItem[] {
    if (canUpdateDigitalProperties) {
      return [
        {
          label: this.translateService.instant('action_edit'),
          clickListener: this.editSelectedDigitalProperty.bind(this, entry._id),
        },
      ];
    }
    return [];
  }

  private getTableData(entries: IDigitalProperty[], canUpdateDigitalProperties: boolean): ITableRow[] {
    return entries.map(
      (entry: IDigitalProperty): ITableRow => ({
        data: {
          [$digitalProperty.name]: NgbTableUtilities.textCell({
            text: entry[$digitalProperty.name],
          }),
          [$digitalProperty.type]: NgbTableUtilities.textCell({
            text: entry[$digitalProperty.type][$digitalPropertyType.name],
          }),
          [$digitalProperty.url]: NgbTableUtilities.textCell({
            text: entry[$digitalProperty.url] ?? '',
          }),
          actions: NgbTableUtilities.dropdownCell({
            ellipsisIcon: true,
            dropdownItems: this.getActionsDropdownItems(entry, canUpdateDigitalProperties),
          }),
        },
      }),
    );
  }

  private getComparable(property: IDigitalProperty, column: string): string {
    if (column === $digitalProperty.type) {
      return property[$digitalProperty.type][$digitalPropertyType.name];
    }

    return property[column];
  }

  private getFilteredProperties(): Observable<IDigitalProperty[]> {
    const updateTableEmptyState = ([properties, searchTerm]: [IDigitalProperty[], string]): void => {
      if (SharedCommonUtility.isNullish(searchTerm) || properties.length === 0) {
        this.tableConfig = {
          ...this.tableConfig,
          emptyState: {
            title: this.translateService.instant('no_digital_properties_in_this_workspace'),
            subtitle: this.translateService.instant(''),
          },
        };
      } else {
        this.tableConfig = {
          ...this.tableConfig,
          emptyState: {
            title: this.translateService.instant('no_digital_properties_found'),
            subtitle: this.translateService.instant('no_digital_properties_found_description'),
          },
        };
      }
    };

    const filterProperties = ([properties, searchTerm]: [IDigitalProperty[], string]): IDigitalProperty[] => {
      if (!SharedCommonUtility.isNullish(searchTerm)) {
        const nameFilter = (property: IDigitalProperty): boolean => {
          return property[$digitalProperty.name].toLowerCase().indexOf(searchTerm.toLowerCase()) > -1;
        };

        return properties?.filter(nameFilter) ?? [];
      }

      return properties ?? [];
    };

    return combineLatest([this.digitalProperties$, this.searchTerm$]).pipe(tap(updateTableEmptyState), map(filterProperties));
  }

  private getSortedProperties(digitalProperties$: Observable<IDigitalProperty[]>): Observable<IDigitalProperty[]> {
    const sortProperties = ([properties, sort, page, pageSize]: [IDigitalProperty[], SortEvent, number, number]): [
      IDigitalProperty[],
      number,
      number,
    ] => {
      if (!SharedCommonUtility.isNullish(sort) && sort.direction !== $sortingOrder.all) {
        const sortFn = (a: IDigitalProperty, b: IDigitalProperty): number => {
          const aValue: string = this.getComparable(a, sort.column);
          const bValue: string = this.getComparable(b, sort.column);

          return aValue.localeCompare(bValue) * (sort.direction === $sortingOrder.desc ? -1 : 1);
        };

        properties.sort(sortFn);
      }

      return [properties, page, pageSize];
    };

    const sliceProperties = ([properties, page, pageSize]: [IDigitalProperty[], number, number]): IDigitalProperty[] => {
      return properties.slice((page - 1) * pageSize, page * pageSize);
    };

    return combineLatest([digitalProperties$, this.sort$, this.page$, this.pageSize$]).pipe(
      map(sortProperties),
      map(sliceProperties),
    );
  }

  public onCreateDigitalProperty(): void {
    this.router
      .navigate([Api.digital_properties, Api.create], { relativeTo: this.activatedRoute })
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  public get canCreateDigitalProperty$(): Observable<boolean> {
    return this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useEntityLevel()
            .useFunctionalActions(RequiredSecurities.Digital_Properties_Create.functionalActions)
            .useForNewSecurityOnly()
            .check(),
        ),
      );
  }

  public get canUpdateDigitalProperties$(): Observable<boolean> {
    return this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useAdmin()
            .useFunctionalActions(RequiredSecurities.Digital_Properties_Update.functionalActions)
            .useForNewSecurityOnly()
            .check(),
        ),
      );
  }

  public editSelectedDigitalProperty(digitalPropertyId: string): void {
    this.router
      .navigate([`${Api.digital_properties}/${digitalPropertyId}`, Api.edit], { relativeTo: this.activatedRoute })
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  public ngOnInit(): void {
    const filteredProperties$: Observable<IDigitalProperty[]> = this.getFilteredProperties();

    this.dpCount$ = filteredProperties$.pipe(
      map((properties: IDigitalProperty[]) => properties.length),
      startWith(0),
    );

    const properties$: Observable<IDigitalProperty[]> = this.getSortedProperties(filteredProperties$);

    this.tableData$ = combineLatest([properties$, this.canUpdateDigitalProperties$]).pipe(
      map(([properties, canUpdateDigitalProperties]: [IDigitalProperty[], boolean]): ITableRow[] => {
        return this.getTableData(properties, canUpdateDigitalProperties);
      }),
    );
  }
}
